/**
 * CG Projects — API client. Centralized HTTP for the API gateway.
 * Mirrors the cg-pos pattern (Bearer token in localStorage, ApiResponse unwrap).
 */

import type { ApiResponse, ApiError } from '@cg-projects/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api/v1';
const TOKEN_KEY = 'cg_projects_token';

/** Stable per-tab id sent with every write; the server echoes it on socket
 *  events so this tab can ignore changes it already applied optimistically. */
export const CLIENT_ID: string =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `c-${Math.random().toString(36).slice(2)}`;

/** Origin of the API server (for the Socket.IO connection). */
export function apiOrigin(): string {
  try {
    return new URL(API_BASE_URL, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  timeout?: number;
}

export class ApiClientError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;

  constructor(error: ApiError, status: number) {
    super(error.message);
    this.name = 'ApiClientError';
    this.code = error.code;
    this.status = status;
    this.details = error.details;
  }
}

let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    // Cookie too: <img>/download requests can't send Bearer headers.
    document.cookie = `${TOKEN_KEY}=${token}; path=/; SameSite=Lax; Secure; max-age=2592000`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    document.cookie = `${TOKEN_KEY}=; path=/; max-age=0`;
  }
}

export function getAuthToken(): string | null {
  if (!authToken) {
    authToken = localStorage.getItem(TOKEN_KEY);
    // Keep the cookie in sync for restored sessions (image/file auth).
    if (authToken) document.cookie = `${TOKEN_KEY}=${authToken}; path=/; SameSite=Lax; Secure; max-age=2592000`;
  }
  return authToken;
}

// The cookie is only stamped at login/boot, so in a PINNED TAB it dies at 12h
// while the Bearer session keeps every API call working — and then images and
// video 401 silently (found live in the 7/27 sweep: a <video> failing with a
// bare error-4 that was really a JSON 401 underneath). Re-stamp on a slow
// heartbeat AND on tab-wake, so an open tab's cookie can never quietly
// predecease its session. Intervals freeze while a tab sleeps; visibilitychange
// covers the morning-after wake-up.
function refreshAuthCookie(): void {
  if (authToken) document.cookie = `${TOKEN_KEY}=${authToken}; path=/; SameSite=Lax; Secure; max-age=2592000`;
}
// Module-scope listeners only exist in a real browser — the unit tests import
// this module under node, where `document` is undefined at load time.
if (typeof document !== 'undefined') {
  setInterval(refreshAuthCookie, 30 * 60 * 1000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshAuthCookie();
  });
}

// Fired when the gateway rejects a request with 401 (expired/revoked token).
// The auth store registers a handler that logs out → the login screen shows,
// instead of the app silently failing every action while looking signed-in.
// The error CODE is passed through because "your 12-hour session ran out" and
// "your access was ended" need different words — telling someone whose access was
// revoked to just sign in again sends them round a loop.
export type UnauthorizedCode = 'AUTH_SESSION_REVOKED' | 'AUTH_TOKEN_EXPIRED' | 'AUTH_TOKEN_INVALID' | 'AUTH_TOKEN_MISSING' | 'UNKNOWN';
let onUnauthorized: ((code: UnauthorizedCode) => void) | null = null;
export function setUnauthorizedHandler(fn: (code: UnauthorizedCode) => void): void {
  onUnauthorized = fn;
}

/** A human message for a failure that never reached our API (proxy/gateway), so
 *  the UI says what actually happened instead of blaming the connection. */
function httpErrorFor(status: number): { code: string; message: string } {
  if (status === 413) return { code: 'TOO_LARGE', message: 'That file is too big to upload. Try one under 15 MB.' };
  if (status === 429) return { code: 'RATE_LIMITED', message: 'Too many requests — wait a moment and try again.' };
  if (status === 502 || status === 503 || status === 504) {
    return { code: 'UPSTREAM', message: 'The server is busy or restarting. Try again in a moment.' };
  }
  return { code: 'UNKNOWN', message: `Something went wrong (HTTP ${status}).` };
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, timeout = 30000 } = options;

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Client-Id': CLIENT_ID,
    ...headers,
  };
  const token = getAuthToken();
  if (token) requestHeaders.Authorization = `Bearer ${token}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timer);

    // NOT response.json(): anything that fails BEFORE our API (an nginx 413 on a
    // big attachment, a 502 from a restarting server) answers with an HTML page,
    // and letting .json() throw dropped us into the catch below — which told the
    // user "Cannot connect to server. Is the API running?" while the server was
    // fine and had returned a perfectly clear status code. That message cost Olivia
    // an afternoon on 2026-07-21. Read the body defensively and keep the status.
    const raw = await response.text();
    let data: ApiResponse<T> | null = null;
    try { data = raw ? (JSON.parse(raw) as ApiResponse<T>) : null; } catch { data = null; }
    if (!response.ok || !data?.success) {
      // Session expired/revoked (12h token, shared tablets left open) — surface
      // it instead of letting the signed-in UI silently fail every action.
      // Ignore 401 on the login call itself (that's just a bad password).
      if (response.status === 401 && !endpoint.startsWith('/auth/login')) {
        onUnauthorized?.((data?.error?.code as UnauthorizedCode | undefined) ?? 'UNKNOWN');
      }
      throw new ApiClientError(data?.error || httpErrorFor(response.status), response.status);
    }
    return data.data as T;
  } catch (error) {
    clearTimeout(timer);
    if (error instanceof ApiClientError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiClientError({ code: 'TIMEOUT', message: 'Request timed out' }, 408);
    }
    throw new ApiClientError(
      { code: 'NETWORK_ERROR', message: 'Cannot connect to server. Is the API running?' },
      0,
    );
  }
}

export const api = {
  get: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'GET' }),
  post: <T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),
  patch: <T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),
  put: <T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),
  delete: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
  /** Raw file download (CSV export). A bare <a href> would 401 - the JWT lives in
   *  a header, not a cookie - so fetch it and hand back a Blob to save. */
  getBlob: async (endpoint: string, timeout = 120000): Promise<Blob> => {
    // The export is the slowest call in the app (a scan-cap-sized live Odoo read), so
    // it gets a longer budget than request()'s 30s — but it MUST still have an abort
    // path, or a stalled connection leaves the download button disabled until reload.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        credentials: 'include',
        signal: controller.signal,
      });
      // Route 401 through the same logout flow every other request uses, instead of a
      // toast that leaves the rest of the UI looking signed in.
      if (res.status === 401) {
        // A blob response body isn't worth parsing for a code; UNKNOWN falls back
        // to the generic "session ended" wording, which is true either way.
        onUnauthorized?.('UNKNOWN');
        throw new ApiClientError({ code: 'UNAUTHORIZED', message: 'Your session expired — please sign in again.' }, 401);
      }
      if (!res.ok) {
        // The server sends JSON errors even on this endpoint; surface the real message.
        const detail = await res.json().catch(() => null) as { error?: { message?: string } } | null;
        throw new ApiClientError(
          { code: 'EXPORT_FAILED', message: detail?.error?.message ?? `Export failed (${res.status})` },
          res.status,
        );
      }
      return await res.blob();
    } catch (err) {
      if (err instanceof ApiClientError) throw err;
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new ApiClientError({ code: 'TIMEOUT', message: 'The export took too long — narrow the filters and try again.' }, 408);
      }
      throw new ApiClientError({ code: 'NETWORK_ERROR', message: 'Cannot reach the server.' }, 0);
    } finally {
      clearTimeout(timer);
    }
  },
};
